import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft } from 'lucide-react';
import { ProfileFilters } from '@/hooks/useProfileFilters';
import { FilterCategory } from './HierarchicalFilterModal';
import { FILTER_OPTIONS, FILTER_LABELS } from '@/types/filter-options';
interface CategoryOptionsModalProps {
  category: FilterCategory;
  filters: ProfileFilters;
  onUpdateFilter: (key: keyof ProfileFilters, value: any) => void;
  onBack: () => void;
  availableInterests?: string[];
}
const CategoryOptionsModal: React.FC<CategoryOptionsModalProps> = ({
  category,
  filters,
  onUpdateFilter,
  onBack,
  availableInterests = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const getCategoryConfig = (category: FilterCategory) => {
    if (category === 'interests') {
      return {
        title: 'Interesses',
        filterKey: 'interests' as keyof ProfileFilters,
        options: availableInterests.map(interest => ({
          value: interest,
          label: interest.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
        }))
      };
    }
    switch (category) {
      case 'gender':
        return {
          title: 'Gênero',
          filterKey: 'gender' as keyof ProfileFilters,
          options: [{
            value: 'male',
            label: 'Masculino'
          }, {
            value: 'female',
            label: 'Feminino'
          }]
        };
      case 'orientation':
        return {
          title: 'Orientação',
          filterKey: 'orientation' as keyof ProfileFilters,
          options: [{
            value: 'straight',
            label: 'Heterossexual'
          }, {
            value: 'gay',
            label: 'Homossexual'
          }, {
            value: 'bisexual',
            label: 'Bissexual'
          }]
        };
      case 'relationshipTypes':
        return {
          title: 'Tipo de Relacionamento',
          filterKey: 'relationshipTypes' as keyof ProfileFilters,
          options: [{
            value: 'casual',
            label: 'Casual'
          }, {
            value: 'serious',
            label: 'Sério'
          }]
        };
      case 'estadoCivil':
        return {
          title: 'Estado Civil',
          filterKey: 'estadoCivil' as keyof ProfileFilters,
          options: FILTER_OPTIONS.maritalStatus.map(value => ({
            value,
            label: FILTER_LABELS.maritalStatus[value]
          }))
        };
      case 'alturas':
        return {
          title: 'Altura',
          filterKey: 'alturas' as keyof ProfileFilters,
          options: FILTER_OPTIONS.height.map(value => ({
            value,
            label: FILTER_LABELS.height[value]
          }))
        };
      case 'tiposCorpo':
        return {
          title: 'Tipo Corporal',
          filterKey: 'tiposCorpo' as keyof ProfileFilters,
          options: FILTER_OPTIONS.bodyType.map(value => ({
            value,
            label: FILTER_LABELS.bodyType[value]
          }))
        };
      case 'coresOlhos':
        return {
          title: 'Cor dos Olhos',
          filterKey: 'coresOlhos' as keyof ProfileFilters,
          options: FILTER_OPTIONS.eyeColor.map(value => ({
            value,
            label: FILTER_LABELS.eyeColor[value]
          }))
        };
      case 'coresCabelo':
        return {
          title: 'Cor do Cabelo',
          filterKey: 'coresCabelo' as keyof ProfileFilters,
          options: FILTER_OPTIONS.hairColor.map(value => ({
            value,
            label: FILTER_LABELS.hairColor[value]
          }))
        };
      case 'fumante':
        return {
          title: 'Fumante',
          filterKey: 'fumante' as keyof ProfileFilters,
          options: FILTER_OPTIONS.smoker.map(value => ({
            value,
            label: FILTER_LABELS.smoker[value]
          }))
        };
      case 'bebidas':
        return {
          title: 'Bebidas',
          filterKey: 'bebidas' as keyof ProfileFilters,
          options: FILTER_OPTIONS.drinker.map(value => ({
            value,
            label: FILTER_LABELS.drinker[value]
          }))
        };
      case 'tatuagens':
        return {
          title: 'Tatuagens',
          filterKey: 'tatuagens' as keyof ProfileFilters,
          options: FILTER_OPTIONS.tattoos.map(value => ({
            value,
            label: FILTER_LABELS.tattoos[value]
          }))
        };
      case 'piercings':
        return {
          title: 'Piercings',
          filterKey: 'piercings' as keyof ProfileFilters,
          options: FILTER_OPTIONS.piercings.map(value => ({
            value,
            label: FILTER_LABELS.piercings[value]
          }))
        };
      default:
        return {
          title: 'Categoria',
          filterKey: 'gender' as keyof ProfileFilters,
          options: []
        };
    }
  };
  const config = getCategoryConfig(category);
  const currentValues = filters[config.filterKey] as string[] || [];

  // Filter options based on search term for interests
  const filteredOptions = useMemo(() => {
    if (category !== 'interests' || !searchTerm.trim()) {
      return config.options;
    }
    return config.options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [config.options, searchTerm, category]);
  const handleCheckboxChange = (value: string, checked: boolean) => {
    const newValues = checked ? [...currentValues, value] : currentValues.filter(v => v !== value);
    onUpdateFilter(config.filterKey, newValues);
  };
  const isChecked = (value: string): boolean => {
    return currentValues.includes(value);
  };
  return <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-white/70 hover:text-white hover:bg-white/10 p-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-white font-medium">{config.title}</h2>
        </div>
      </div>

      {/* Search input for interests */}
      {category === 'interests' && <div className="p-4 border-b border-white/10">
          <Input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar interesses..." className="bg-black/40 border-white/20 text-white placeholder:text-white/50" />
        </div>}

      {/* Options */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {filteredOptions.length > 0 ? filteredOptions.map(option => <div key={option.value} className="flex items-center space-x-3">
                <Checkbox id={`${category}-${option.value}`} checked={isChecked(option.value)} onCheckedChange={checked => handleCheckboxChange(option.value, !!checked)} className="border-white/30 data-[state=checked]:bg-luxury-red data-[state=checked]:border-luxury-red" />
                <Label htmlFor={`${category}-${option.value}`} className="text-white/80 text-sm flex-1 cursor-pointer my-[8px]">
                  {option.label}
                </Label>
              </div>) : <p className="text-white/60 text-sm text-center py-4">
              {category === 'interests' && searchTerm ? `Nenhum interesse encontrado para "${searchTerm}"` : 'Nenhuma opção disponível'}
            </p>}
        </div>
      </ScrollArea>
    </div>;
};
export default CategoryOptionsModal;